import type { ExercisePerformance, PrescriptionSnapshot } from "./types";

export interface ProgressionSuggestion {
  kind: "increase" | "hold" | "manual" | "none";
  label: string;
  nextLoad?: number;
  nextStep?: string;
}

export function getProgressionSuggestion(
  prescription: PrescriptionSnapshot,
  performance?: ExercisePerformance,
): ProgressionSuggestion {
  if (!performance || performance.sets.length === 0) {
    return { kind: "none", label: "Première séance" };
  }
  if (prescription.progressionMode === "manual") {
    return { kind: "manual", label: "À décider manuellement" };
  }
  if (prescription.progressionMode === "none") {
    return { kind: "none", label: "Aucune progression automatique" };
  }

  const completed = performance.sets.filter((set) => set.completedAt);
  const expectedSets = prescription.minSetCount ?? prescription.setCount;
  const rirRespected = completed.every(
    (set) =>
      set.rir == null ||
      prescription.targetRirMin == null ||
      set.rir >= prescription.targetRirMin,
  );
  const reachedTop =
    completed.length >= expectedSets &&
    completed.every((set) =>
      prescription.trackingMode === "duration"
        ? (set.duration ?? 0) >= (prescription.maxDuration ?? Infinity)
        : (set.reps ?? 0) >= (prescription.maxReps ?? Infinity),
    );

  if (!reachedTop || !rirRespected) {
    return { kind: "hold", label: "Conserver la charge" };
  }

  if (prescription.progressionMode === "discrete") {
    const nextIndex = (prescription.discreteStepIndex ?? 0) + 1;
    const nextStep = prescription.discreteSteps?.[nextIndex];
    return nextStep
      ? { kind: "increase", label: `Passer à ${nextStep}`, nextStep }
      : { kind: "hold", label: "Dernier palier atteint" };
  }

  const increment = prescription.loadIncrement || 0;
  const actualLoad = completed[0]?.load ?? prescription.load;
  if (completed.some((set) => set.load !== actualLoad)) return { kind: "hold", label: "Stabiliser la charge entre les séries" };
  return increment > 0
    ? {
        kind: "increase",
        label: `Essayer ${formatLoad(actualLoad + increment)} ${prescription.unit}`,
        nextLoad: actualLoad + increment,
      }
    : { kind: "hold", label: "Conserver la charge" };
}

const loadFormatter = new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 2 });
export const formatLoad = (value: number) => loadFormatter.format(value);
